import React, { useState } from 'react';
import styled from 'styled-components';
import { FaBell, FaMoon, FaLanguage, FaCreditCard, FaShieldAlt, FaQuestionCircle } from 'react-icons/fa';
import { useLanguage } from '../../contexts/LanguageContext';

const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const SettingsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SettingsSection = styled.div`
  background: var(--bg-card);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
`;

const SettingsItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: var(--bg-hover);
  }
`;

const ItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: var(--text-primary);
  
  svg {
    font-size: 1.25rem;
    color: var(--primary-color);
  }
`;

const ItemRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  
  svg {
    font-size: 1.25rem;
  }
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
    
    &:checked + span {
      background-color: var(--primary-color);
      
      &:before {
        transform: translateX(26px);
      }
    }
  }
  
  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--border-color);
    transition: .4s;
    border-radius: 24px;
    
    &:before {
      position: absolute;
      content: "";
      height: 16px;
      width: 16px;
      left: 4px;
      bottom: 4px;
      background-color: white;
      transition: .4s;
      border-radius: 50%;
    }
  }
`;

const Settings: React.FC = () => {
  const { t } = useLanguage();
  const [notifications, setNotifications] = useState(true);
  const [darkMode, setDarkMode] = useState(true);
  
  const settingsSections = [
    {
      title: t('app.preferences'),
      items: [
        {
          icon: <FaBell />,
          label: t('app.notifications'),
          right: (
            <ToggleSwitch>
              <input
                type="checkbox"
                checked={notifications}
                onChange={() => setNotifications(!notifications)}
              />
              <span />
            </ToggleSwitch>
          )
        },
        {
          icon: <FaMoon />,
          label: t('app.darkMode'),
          right: (
            <ToggleSwitch>
              <input
                type="checkbox"
                checked={darkMode}
                onChange={() => setDarkMode(!darkMode)}
              />
              <span />
            </ToggleSwitch>
          )
        },
        {
          icon: <FaLanguage />,
          label: t('app.language'),
          right: <span>Français</span>
        }
      ]
    },
    {
      title: t('app.account'),
      items: [
        {
          icon: <FaCreditCard />,
          label: t('app.paymentMethods'),
          right: <span>•••• 4242</span>
        },
        {
          icon: <FaShieldAlt />,
          label: t('app.privacy'),
          right: null
        }
      ]
    },
    {
      title: t('app.support'),
      items: [
        {
          icon: <FaQuestionCircle />,
          label: t('app.help'),
          right: null
        }
      ]
    }
  ];
  
  return (
    <SettingsContainer>
      <Header>
        <Title>{t('app.settings')}</Title>
      </Header>
      
      <SettingsList>
        {settingsSections.map((section, index) => (
          <SettingsSection key={index}>
            <SectionTitle>{section.title}</SectionTitle>
            {section.items.map((item, itemIndex) => (
              <SettingsItem key={itemIndex}>
                <ItemLeft>
                  {item.icon}
                  <span>{item.label}</span>
                </ItemLeft>
                <ItemRight>
                  {item.right}
                </ItemRight>
              </SettingsItem>
            ))}
          </SettingsSection>
        ))}
      </SettingsList>
    </SettingsContainer>
  );
};

export default Settings; 